import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { toast } from 'sonner';
import { Pencil, Trash2, Save } from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

const TYPES = [
  'virement_recu', 'virement_emis', 'paiement_carte',
  'frais', 'remboursement', 'autre',
];

export default function AdminTransactions() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get('/admin/transactions');
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette transaction ? Le solde du client sera mis à jour.')) return;
    try {
      await api.delete(`/admin/transactions/${id}`);
      toast.success('Transaction supprimée');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const openEdit = (t) => {
    setEditing({
      id: t.id,
      type: t.type,
      libelle: t.libelle,
      montant: String(t.montant),
      date_transaction: t.date_transaction ? new Date(t.date_transaction).toISOString().slice(0, 10) : '',
    });
  };

  const save = async () => {
    if (!editing.libelle || !editing.montant) {
      toast.error('Libellé et montant requis');
      return;
    }
    try {
      await api.put(`/admin/transactions/${editing.id}`, {
        type: editing.type,
        libelle: editing.libelle,
        montant: parseFloat(editing.montant),
        date_transaction: editing.date_transaction || null,
      });
      toast.success('Transaction mise à jour');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold">{t('admin.transactionsTitle')}</h1>
      <p className="text-sm text-slate-500">{t('admin.transactionsSubtitle')}</p>

      <div className="bg-white rounded-2xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Client</th>
                <th className="text-left p-3">Libellé</th>
                <th className="text-center p-3">Type</th>
                <th className="text-right p-3">Montant</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.length === 0 && (
                <tr><td colSpan="6" className="p-8 text-center text-slate-500">Aucune transaction</td></tr>
              )}
              {items.map((tx) => (
                <tr key={tx.id} className="hover:bg-slate-50">
                  <td className="p-3 text-xs">{new Date(tx.date_transaction).toLocaleDateString('fr-FR')}</td>
                  <td className="p-3 text-xs font-mono">{tx.user_id?.slice(0, 8)}…</td>
                  <td className="p-3">{tx.libelle}</td>
                  <td className="p-3 text-center text-xs"><Badge variant="outline">{tx.type}</Badge></td>
                  <td className={`p-3 text-right font-bold ${tx.montant >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {tx.montant.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex justify-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(tx)} data-testid={`btn-edit-tx-${tx.id}`}>
                        <Pencil className="w-3.5 h-3.5"/>
                      </Button>
                      <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => remove(tx.id)} data-testid={`btn-del-tx-${tx.id}`}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal édition */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{t('common.edit')}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 py-3">
              <div>
                <Label>Type</Label>
                <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                  <SelectTrigger data-testid="edit-tx-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((ty) => <SelectItem key={ty} value={ty}>{ty}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Libellé</Label>
                <Input value={editing.libelle} onChange={(e) => setEditing({ ...editing, libelle: e.target.value })} data-testid="edit-tx-libelle"/>
              </div>
              <div>
                <Label>Montant (positif ou négatif)</Label>
                <Input type="number" step="0.01" value={editing.montant} onChange={(e) => setEditing({ ...editing, montant: e.target.value })} data-testid="edit-tx-montant"/>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={editing.date_transaction} onChange={(e) => setEditing({ ...editing, date_transaction: e.target.value })} data-testid="edit-tx-date"/>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
            <Button onClick={save} className="bg-blue-700" data-testid="btn-save-tx">
              <Save className="w-4 h-4 mr-1"/> {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
